// src/modules/app-views/_apps/appmulti/orders/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useMemo } from 'react';
import { Tabs } from 'antd';
import { DollarOutlined, AuditOutlined } from '@ant-design/icons';
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { buildOriginIcon } from 'components/JarvislyComponents/DocumentInfoIcons';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import translateX from 'utils/translateX';

import OrderTab from './tabs/OrderTab';
import SummaryTab from './tabs/SummaryTab';
import VoucherTab from './tabs/VoucherTab';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const isMockStep = false;

const STYLES = {
  cardHeaderWrapper: { position: 'relative' },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const OrdersDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode } =
    useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { formRef, isInitializedRef } = useFormInstance({ data });

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const itemsLocal = buildTabsArray({ ...props, data, initTab, viewMode });
    buildTabs(itemsLocal);
  }, [initTab, viewMode, data?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={null}
          showSubmitButton={false}
          reloadEntirePage={false}
          locked
          showInfoMessage={false}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={STYLES.cardHeaderWrapper}>
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.__buyer?.name}
              watchField="countryCode"
            />
            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default OrdersDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, data } = props;
  const isFreshNew = !data?._id;

  return [
    {
      key: initTab,
      icon: buildOriginIcon(data),
      label: <span>{translateX('order')}</span>,
      hide: false,
      disabled: false,
      children: <OrderTab />,
    },
    {
      key: 'voucher',
      icon: <AuditOutlined />,
      label: <span>{translateX('vouchers')}</span>,
      hide: false,
      disabled: isFreshNew,
      children: <VoucherTab />,
    },
    {
      key: 'summary',
      icon: <DollarOutlined />,
      label: <span>{translateX('values_summary')}</span>,
      hide: false,
      disabled: isFreshNew,
      children: <SummaryTab />,
    },
  ];
}

